import * as React from 'react'
import { Route, Routes } from 'react-router-dom'

import { FinanceWorkspaceModuleRoutesVisibilityDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { buildNavigation } from 'modules/core/lib/react-router'
import { buildModuleRoute } from 'modules/core/lib/react-router'

import { financeAccountingRoutes } from './modularNavigation/accounting'
import {
  legacyFinanceRedirectRoutes,
  legacySpendingProductsRedirectRoutes,
} from './modularNavigation/legacyRedirects'
import { financeSalesRoutes } from './modularNavigation/sales'
import { financeSpendingRoutes } from './modularNavigation/spending'
import { financeTreasuryRoutes } from './modularNavigation/treasury'

const ReroutingIndex = React.lazy(() => import('modules/finance/pages/ReroutingIndex'))

const FinancialWorkspacePayrollPage = React.lazy(
  () => import('modules/finance/pages/FinancialWorkspacePage/FinanceWorkspacePayroll')
)

const FinancialWorkspaceCompensationPage = React.lazy(
  () => import('modules/finance/pages/FinancialWorkspacePage/FinanceWorkspaceCompensation')
)

const FinancialWorkspaceEmployeeCostsPage = React.lazy(
  () => import('modules/finance/pages/FinancialWorkspacePage/FinancialWorkspaceEmployeeCosts')
)

const ImportPayrollResultModalPage = React.lazy(
  () => import('modules/finance/pages/FinancialWorkspacePage/ImportPayrollResultModal')
)

const FinancialWorkspaceRootPage = React.lazy(
  () => import('modules/finance/pages/FinancialWorkspacePage/RootPage')
)

export const financeModularRoutes = buildNavigation((navigation) => {
  return [
    {
      path: navigation.finance.path,
      handle: {
        module: 'finance-workspace',
        moduleName: () => i18n.t('finance.navigator.description'),
      },
      children: [
        buildModuleRoute({
          path: navigation.finance.financialWorkspace.path,
          element: <FinancialWorkspaceRootPage />,
          handle: {
            module: 'finance-workspace',
            moduleName: () => i18n.t('sidebar.finance_workspace'),
            meta: ({ watchQuery }) => {
              return watchQuery({
                query: FinanceWorkspaceModuleRoutesVisibilityDocument,
              }).map(({ data }) => {
                const canSeeFinancialWorkspace =
                  data?.financialWorkspace?._policies?.seeFinancialWorkspace?.can ?? false
                const canSeePayroll = data?.financialWorkspace?._policies?.payrollSee?.can ?? false
                const canSeeCompensations =
                  data?.financialWorkspace?._policies?.compensationsSee?.can ?? false
                const payrollSummaryUploadedLastYear =
                  data?.financialWorkspace.payrollLandingBannersConnection.edges[0]?.node
                    .payrollSummaryUploadedLastYear ?? false

                return {
                  subNavigation: [
                    {
                      id: 'employee-costs',
                      title: i18n.t(
                        'finance.financial_workspace.subNavigation.employee_costs.title'
                      ),
                      href: navigation.finance.financialWorkspace.employee_costs.fullPath(),
                      isVisible: canSeeFinancialWorkspace,
                    },
                    {
                      id: 'payroll',
                      title: i18n.t('finance.financial_workspace.subNavigation.payroll_data.title'),
                      href: navigation.finance.financialWorkspace.payroll.fullPath(),
                      isVisible:
                        canSeeFinancialWorkspace &&
                        payrollSummaryUploadedLastYear &&
                        canSeePayroll &&
                        !canSeeCompensations,
                    },
                    {
                      id: 'compensations',
                      title: i18n.t('finance.financial_workspace.compensations.compensation'),
                      href: navigation.finance.financialWorkspace.compensations.fullPath(),
                      isVisible: canSeeFinancialWorkspace && canSeeCompensations,
                    },
                  ],
                }
              })
            },
          },
          children: [
            {
              index: true,
              element: <ReroutingIndex />,
            },
            {
              handle: {
                meta: () => ({
                  title: i18n.t('finance.financial_workspace.subNavigation.employee_costs.title'),
                }),
              },
              path: navigation.finance.financialWorkspace.employee_costs.path,
              element: <FinancialWorkspaceEmployeeCostsPage />,
              children: [
                {
                  path: navigation.finance.financialWorkspace.employee_costs.import_result.path,
                  element: (
                    <Routes>
                      <Route path="" element={<ImportPayrollResultModalPage />} />
                    </Routes>
                  ),
                },
              ],
            },
            {
              handle: {
                meta: () => ({
                  title: i18n.t('finance.financial_workspace.subNavigation.payroll_data.title'),
                }),
              },
              path: navigation.finance.financialWorkspace.payroll.path,
              element: <FinancialWorkspacePayrollPage />,
              children: [
                {
                  path: navigation.finance.financialWorkspace.payroll.import_result.path,
                  element: <ImportPayrollResultModalPage />,
                },
              ],
            },
            {
              handle: {
                meta: () => ({
                  title: i18n.t('finance.financial_workspace.compensations.compensation'),
                }),
              },
              path: navigation.finance.financialWorkspace.compensations.path,
              element: <FinancialWorkspaceCompensationPage />,
              children: [
                {
                  path: navigation.finance.financialWorkspace.compensations.import_result.path,
                  element: (
                    <Routes>
                      <Route path="" element={<ImportPayrollResultModalPage />} />
                    </Routes>
                  ),
                },
              ],
            },
          ],
        }),
        ...financeSpendingRoutes(navigation),
        ...financeAccountingRoutes(navigation),
        ...financeSalesRoutes(navigation),
        ...financeTreasuryRoutes(navigation),
        ...legacyFinanceRedirectRoutes(navigation),
      ],
    },
    ...legacySpendingProductsRedirectRoutes(navigation),
  ]
})
